import React from 'react';
import {
	Card, Row, Col, Form, Input, InputNumber, Select, Button, message, Typography,
	Tooltip, Alert, Spin,
} from 'antd';
import {
	SaveOutlined, RedoOutlined, GithubOutlined, CloudServerOutlined,
	WhatsAppOutlined, ApiOutlined, SettingOutlined, InfoCircleOutlined,
	RocketOutlined,
} from '@ant-design/icons';
import moment from 'moment';

import Config from '../../Config';

import { connect } from 'react-redux';
import {
	initHandler,
	ajaxViewHandler,
	ajaxHandler,
	setConfig,
} from "../../store/actions";
import { Helmet } from "react-helmet";
import ErrorAlertModal from '../../components/ErrorAlertModal';

const { Text } = Typography;
const { TextArea } = Input;

const configClass = {
	title: 'Konfigurasi App Cetak',
	apiUrl: {
		data: 'addons/api/secrets/print-client-config/',
	},
	breadCrumb: [
		<span><SettingOutlined /> Pengaturan Secrets</span>,
		'Konfigurasi App Cetak',
	],
};

const config = new Config();

// Default WAHA (CONFIRMED user 2026-10-19) - dipakai utk render awal sebelum
// data server datang & utk tombol "Pulihkan Default" di bawah.
const DEFAULT_WA_BODY_TEMPLATE = {
	chatId: '{{GROUP_ID}}',
	id: null,
	mentions: null,
	reply_to: null,
	text: '{{MESSAGE}}',
	linkPreview: true,
	linkPreviewHighQuality: false,
	session: '',
};

// Fase 7 (version-gating bertahap) - 3 mode, urutan rollout aman yang
// disarankan: off -> warn -> block (lihat Alert di section terkait).
const ENFORCEMENT_OPTIONS = [
	{ value: 'off', label: 'Off - tidak ada penegakan' },
	{ value: 'warn', label: 'Warn - catat ke activity log, tidak blokir' },
	{ value: 'block', label: 'Block - device di bawah minimum ditolak' },
];

const FIELD_STYLE = { borderRadius: 8, height: 40 };

const SECTION_STYLE = {
	border: '1px solid #eef1f6',
	borderRadius: 12,
	padding: '18px 20px 6px',
	marginBottom: 20,
	background: '#fff',
};

function SectionTitle({ icon, title, subtitle }) {
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
			<div style={{
				width: 34, height: 34, borderRadius: 9, flexShrink: 0,
				background: 'linear-gradient(135deg, #0f284714, #1e4d8a14)',
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				color: '#1e4d8a', fontSize: 16,
			}}>
				{icon}
			</div>
			<div>
				<div style={{ fontWeight: 700, fontSize: 14, color: '#0f2847' }}>{title}</div>
				{subtitle && <div style={{ fontSize: 12, color: '#8a94a6' }}>{subtitle}</div>}
			</div>
		</div>
	);
}

class FormPrintClientConfig extends React.Component {
	formRef = React.createRef();

	constructor(props) {
		super(props);
		this.state = {
			errTitle: '',
			errMsg: '',
			loadingData: true,
			loadingSave: false,
			isMobile: window.innerWidth <= 768,

			waBodyTemplateText: JSON.stringify(DEFAULT_WA_BODY_TEMPLATE, null, 2),
			waBodyTemplateError: '',

			updatedAt: null,
			enforcementModePreview: 'off',
		};
	}

	UNSAFE_componentWillMount() {
		this.props.initHandler();
	}

	componentDidMount() {
		this.loadData();
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
	}

	loadData = () => {
		this.setState({ loadingData: true });
		this.props.ajaxViewHandler('get', configClass.apiUrl.data)
			.then(() => {
				if (this.props.isSuccess) {
					const data = this.props.responseMsg;
					this.formRef.current && this.formRef.current.setFieldsValue({
						github_token: data.github_token,
						netbird_management_url: data.netbird_management_url,
						mqtt_broker: data.mqtt_broker,
						mqtt_port: data.mqtt_port,
						wa_group_id: data.wa_group_id,
						wa_api_key: data.wa_api_key,
						wa_url: data.wa_url,
						wa_api_key_header: data.wa_api_key_header,
						min_kutipan_version: data.min_kutipan_version,
						min_notice_version: data.min_notice_version,
						enforcement_mode: data.enforcement_mode || 'off',
					});
					this.setState({
						waBodyTemplateText: JSON.stringify(data.wa_body_template || DEFAULT_WA_BODY_TEMPLATE, null, 2),
						waBodyTemplateError: '',
						updatedAt: data.updated_at || null,
						enforcementModePreview: data.enforcement_mode || 'off',
					});
				} else {
					this.setState({ errTitle: 'Gagal memuat data', errMsg: this.props.responseMsg });
				}
				this.setState({ loadingData: false });
			})
			.catch(() => {
				this.setState({ loadingData: false });
				message.error({ content: 'Kesalahan jaringan', duration: 2, key: 'toast' });
			});
	}

	handleResetTemplate = () => {
		this.setState({
			waBodyTemplateText: JSON.stringify(DEFAULT_WA_BODY_TEMPLATE, null, 2),
			waBodyTemplateError: '',
		});
	}

	handleTemplateChange = (e) => {
		this.setState({ waBodyTemplateText: e.target.value });
	}

	handleValuesChange = (changedValues) => {
		if ('enforcement_mode' in changedValues) {
			this.setState({ enforcementModePreview: changedValues.enforcement_mode });
		}
	}

	handleSubmit = (values) => {
		let wa_body_template;
		try {
			wa_body_template = JSON.parse(this.state.waBodyTemplateText);
		} catch (e) {
			this.setState({ waBodyTemplateError: 'Format JSON tidak valid: ' + e.message });
			message.error({ content: 'Template body WA bukan JSON valid, periksa kembali', duration: 3, key: 'toast' });
			return;
		}
		this.setState({ waBodyTemplateError: '' });

		const payload = { ...values, wa_body_template };

		this.setState({ loadingSave: true });
		message.loading({ content: 'Menyimpan konfigurasi...', key: 'toast' });
		this.props.ajaxHandler('put', configClass.apiUrl.data, payload, false, true)
			.then(() => {
				this.setState({ loadingSave: false });
				if (this.props.isSuccess) {
					message.success({ content: 'Konfigurasi berhasil disimpan', duration: 2, key: 'toast' });
					const data = this.props.responseMsg;
					this.setState({ updatedAt: data.updated_at || null });
				} else {
					message.destroy();
					this.setState({ errTitle: 'Gagal menyimpan konfigurasi', errMsg: this.props.responseMsg });
				}
			})
			.catch(() => {
				this.setState({ loadingSave: false });
				message.error({ content: 'Kesalahan jaringan', duration: 2, key: 'toast' });
			});
	}

	render() {
		const { errMsg, errTitle, loadingData, loadingSave, isMobile, waBodyTemplateText, waBodyTemplateError, updatedAt, enforcementModePreview } = this.state;
		const colSpan = isMobile ? 24 : 12;

		return (
			<div>
				<Helmet>
					<title>{configClass.title + ' | ' + config.get_site_info().name}</title>
					<style>{`
						.pcc-wrap .ant-form-item-label > label { font-weight: 600; color: #374151; font-size: 13px; }
						.pcc-wrap textarea.pcc-json { font-family: 'Consolas', 'Courier New', monospace; font-size: 12.5px; line-height: 1.6; }
					`}</style>
				</Helmet>

				<ErrorAlertModal
					visible={errMsg !== ''}
					title={errTitle}
					error={errMsg}
					onClose={() => this.setState({ errTitle: '', errMsg: '' })}
				/>

				<Card
					className="pcc-wrap"
					style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden' }}
					bodyStyle={{ padding: 0 }}
				>
					{/* Header */}
					<div style={{
						padding: isMobile ? '14px 16px' : '16px 20px',
						background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
						display: 'flex', alignItems: 'center',
						justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
					}}>
						<div>
							<div style={{ color: '#fff', fontWeight: 700, fontSize: isMobile ? 14 : 17 }}>
								<CloudServerOutlined style={{ marginRight: 8 }} />{configClass.title}
							</div>
							{!isMobile && (
								<div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
									Konfigurasi terpusat untuk kutipan.exe / notice.exe / updater.exe
									{updatedAt && ` · Terakhir diubah ${moment(updatedAt).format('DD-MM-YYYY HH:mm')}`}
								</div>
							)}
						</div>
						<Tooltip title="Muat Ulang">
							<Button
								onClick={this.loadData}
								icon={<RedoOutlined />}
								disabled={loadingData}
								style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}
							>
								{!isMobile && 'Muat Ulang'}
							</Button>
						</Tooltip>
					</div>

					<div style={{ padding: isMobile ? 16 : '24px 28px' }}>
						<Alert
							type="info"
							showIcon
							icon={<InfoCircleOutlined />}
							message="Device (kutipan.exe/notice.exe/updater.exe) mengambil nilai ini otomatis setiap kali dibuka - tidak perlu diketik manual lagi per-device, dan tidak perlu build ulang aplikasi kalau nilainya berubah."
							style={{ marginBottom: 20, borderRadius: 8 }}
						/>

						<Spin spinning={loadingData}>
							<Form
								ref={this.formRef}
								layout="vertical"
								onFinish={this.handleSubmit}
								onValuesChange={this.handleValuesChange}
							>
								{/* ── GitHub & NetBird ── */}
								<div style={SECTION_STYLE}>
									<SectionTitle
										icon={<GithubOutlined />}
										title="Update & NetBird"
										subtitle="Dipakai updater.exe (cek rilis GitHub) & provisioning VPN NetBird"
									/>
									<Row gutter={16}>
										<Col span={colSpan}>
											<Form.Item label="GitHub Token" name="github_token">
												<Input.Password
													placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
													style={FIELD_STYLE}
													autoComplete="new-password"
												/>
											</Form.Item>
										</Col>
										<Col span={colSpan}>
											<Form.Item label="NetBird Management URL" name="netbird_management_url">
												<Input placeholder="https://netbird.example.com" style={FIELD_STYLE} />
											</Form.Item>
										</Col>
									</Row>
								</div>

								{/* ── MQTT / Broker ── */}
								<div style={SECTION_STYLE}>
									<SectionTitle
										icon={<ApiOutlined />}
										title="MQTT / Broker"
										subtitle="Alamat broker yang disubscribe device untuk menerima job cetak"
									/>
									<Row gutter={16}>
										<Col span={colSpan}>
											<Form.Item label="MQTT Broker (host/IP)" name="mqtt_broker">
												<Input placeholder="broker.example.com" style={FIELD_STYLE} />
											</Form.Item>
										</Col>
										<Col span={colSpan}>
											<Form.Item label="MQTT Port" name="mqtt_port">
												<InputNumber placeholder="8883" style={{ ...FIELD_STYLE, width: '100%' }} min={1} max={65535} />
											</Form.Item>
										</Col>
									</Row>
								</div>

								{/* ── WhatsApp Gateway ── */}
								<div style={SECTION_STYLE}>
									<SectionTitle
										icon={<WhatsAppOutlined />}
										title="Notifikasi WhatsApp (Gateway Dinamis)"
										subtitle="Eskalasi printer gagal & tombol 'Laporkan ke Admin' - bisa pindah gateway tanpa update aplikasi"
									/>
									<Row gutter={16}>
										<Col span={colSpan}>
											<Form.Item label="Group ID / Chat ID Tujuan" name="wa_group_id">
												<Input placeholder="1203xxxxxxxxxxxx@g.us" style={FIELD_STYLE} />
											</Form.Item>
										</Col>
										<Col span={colSpan}>
											<Form.Item label="API Key Gateway WA" name="wa_api_key">
												<Input.Password placeholder="API key gateway WA" style={FIELD_STYLE} autoComplete="new-password" />
											</Form.Item>
										</Col>
										<Col span={colSpan}>
											<Form.Item label="URL Endpoint Gateway" name="wa_url">
												<Input placeholder="https://waha.example.com/api/sendText" style={FIELD_STYLE} />
											</Form.Item>
										</Col>
										<Col span={colSpan}>
											<Form.Item label="Nama Header Auth" name="wa_api_key_header">
												<Input placeholder="X-API-KEY" style={FIELD_STYLE} />
											</Form.Item>
										</Col>
									</Row>

									<Form.Item
										label={
											<span>
												Template Body (JSON) &nbsp;
												<Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
													placeholder <Text code>{'{{GROUP_ID}}'}</Text> & <Text code>{'{{MESSAGE}}'}</Text> disubstitusi otomatis saat kirim
												</Text>
											</span>
										}
										validateStatus={waBodyTemplateError ? 'error' : undefined}
										help={waBodyTemplateError || undefined}
									>
										<TextArea
											className="pcc-json"
											value={waBodyTemplateText}
											onChange={this.handleTemplateChange}
											autoSize={{ minRows: 6, maxRows: 14 }}
											style={{ borderRadius: 8 }}
										/>
									</Form.Item>
									<Button
										size="small"
										icon={<RedoOutlined />}
										onClick={this.handleResetTemplate}
										style={{ borderRadius: 8, marginBottom: 12 }}
									>
										Pulihkan Template Default (WAHA)
									</Button>
								</div>

								{/* ── Version-Gating Bertahap (Fase 7) ── */}
								<div style={SECTION_STYLE}>
									<SectionTitle
										icon={<RocketOutlined />}
										title="Version-Gating Bertahap"
										subtitle="Paksa device pindah dari versi lama - bisa diuji coba tanpa resiko (off -> warn -> block)"
									/>
									<Row gutter={16}>
										<Col span={colSpan}>
											<Form.Item label="Versi Minimum Kutipan" name="min_kutipan_version">
												<Input placeholder="Contoh: 2.0.0 (kosong = tidak digate)" style={FIELD_STYLE} />
											</Form.Item>
										</Col>
										<Col span={colSpan}>
											<Form.Item label="Versi Minimum Notice" name="min_notice_version">
												<Input placeholder="Contoh: 2.0.0 (kosong = tidak digate)" style={FIELD_STYLE} />
											</Form.Item>
										</Col>
									</Row>
									<Form.Item label="Mode Penegakan Global" name="enforcement_mode">
										<Select options={ENFORCEMENT_OPTIONS} style={{ borderRadius: 8 }} />
									</Form.Item>

									{enforcementModePreview === 'off' && (
										<Alert
											type="success"
											showIcon
											message="Off - zero risk, perilaku SEMUA device sama sekali tidak berubah. Aman dijalankan kapan saja."
											style={{ borderRadius: 8, marginBottom: 8 }}
										/>
									)}
									{enforcementModePreview === 'warn' && (
										<Alert
											type="warning"
											showIcon
											message="Warn - tidak ada device yang diblokir. Device di bawah minimum/tidak kirim versi akan DICATAT ke Activity Log (ExternalActivityLog) untuk visibility, sebelum berani pindah ke Block."
											style={{ borderRadius: 8, marginBottom: 8 }}
										/>
									)}
									{enforcementModePreview === 'block' && (
										<Alert
											type="error"
											showIcon
											message="Block - device di bawah minimum versi (atau tidak mengirim versi sama sekali) AKAN DITOLAK cek-mac-address. Pastikan sudah diuji di mode Warn dulu. Updater.exe selalu dikecualikan dari gate ini (supaya device yang ter-block tetap bisa update sendiri)."
											style={{ borderRadius: 8, marginBottom: 8 }}
										/>
									)}
									<Text type="secondary" style={{ fontSize: 12 }}>
										Override per-device/UPT (termasuk pengecualian dua arah) diatur lewat menu{' '}
										<Text strong>Master &gt; Perangkat / MAC Address</Text> (field "Override Enforcement").
									</Text>
								</div>

								<div style={{
									display: 'flex',
									justifyContent: isMobile ? 'stretch' : 'flex-end',
									flexDirection: isMobile ? 'column' : 'row',
									gap: 10,
									marginTop: 8,
								}}>
									<Button
										type="primary"
										htmlType="submit"
										icon={<SaveOutlined />}
										loading={loadingSave}
										style={{
											background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
											border: 'none', borderRadius: 8, fontWeight: 600,
											height: 42, width: isMobile ? '100%' : 180,
										}}
									>
										Simpan Konfigurasi
									</Button>
								</div>
							</Form>
						</Spin>
					</div>
				</Card>
			</div>
		);
	}
}

const mapStateToProps = (state) => state.configReducers;

export default connect(mapStateToProps, {
	initHandler, setConfig, ajaxViewHandler, ajaxHandler,
})(FormPrintClientConfig);
